import React from "react";
import { BrandingContext } from "@/branding/BrandingProvider";

type BrandLogoProps = {
  /** Outer box size in px (width = height). */
  size?: number;
  /** Shape of the container around the logo. */
  shape?: "circle" | "rounded" | "square";
  /** Background color behind the image/initial. */
  background?: string;
  /** Add a subtle drop shadow. */
  shadow?: boolean;
  /** Draw a ring/border around the logo. */
  ring?: boolean;
  /** Optional ring color; defaults to brand primary. */
  ringColor?: string;
  /** Extra className / style passthroughs. */
  className?: string;
  style?: React.CSSProperties;
  /** Props forwarded to the <img> element. */
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
};

/** Brand-aware logo with graceful fallback to the first letter of the app/short name. */
export default function BrandLogo({
  size = 68,
  shape = "circle",
  background = "#fff",
  shadow = true,
  ring = false,
  ringColor,
  className,
  style,
  imgProps,
}: BrandLogoProps) {
  const branding = React.useContext(BrandingContext);

  const src = branding?.assets?.logo || "";
  const initial =
    (branding?.shortName || branding?.appName || "A")
      .trim()
      .charAt(0)
      .toUpperCase();

  const radius =
    shape === "circle" ? "50%" : shape === "rounded" ? 16 : 0;

  const border =
    ring ? `2px solid ${ringColor || branding?.primaryColor || "#1976d2"}` : "0";

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        boxShadow: shadow ? "0 1px 6px rgba(0,0,0,0.15)" : "none",
        border,
        ...style,
      }}
      aria-label="brand-logo"
    >
      {src ? (
        <img
          src={src}
          alt={imgProps?.alt || "Brand Logo"}
          width={size}
          height={size}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
          decoding="async"
          loading="eager"
          {...imgProps}
        />
      ) : (
        <span
          style={{
            fontWeight: 800,
            fontSize: Math.round(size * 0.42),
            lineHeight: 1,
            color: branding?.primaryColor || "#222",
            userSelect: "none",
          }}
        >
          {initial}
        </span>
      )}
    </div>
  );
}
